import React from 'react';
import { BrowserRouter as Router, Routes, Route, Link } from 'react-router-dom';
import { HomePage } from './components/HomePage/HomePage';
import { TournamentList } from './components/TournamentList/TournamentList';
import { TournamentDetail } from './components/TournamentDetail/TournamentDetail';
import { PersonList } from './components/PersonList/PersonList';
import Privacy from './components/Legal/Privacy';
import Terms from './components/Legal/Terms';
import NotFound from './components/NotFound/NotFound';
import { AuthProvider, useAuth } from './components/Auth/AuthContext';
import Login from './components/Auth/Login';
import Register from './components/Auth/Register';
import AuthGuard from './components/Auth/AuthGuard';
import LoginRequired from './components/Auth/LoginRequired';
import UpdatePerson from './components/UpdatePerson/UpdatePerson';
import AdminDashboard from './components/AdminDashboard/AdminDashboard';
import PersonDetail from './components/PersonDetail/PersonDetail';
import UpdateTournament from './components/UpdateTournament/UpdateTournament';
import TournamentResults from './components/TournamentResults/TournamentResults';
import CreateTournament from './components/CreateTournament/CreateTournament';
import BracketView from './components/BracketView/BracketView';
import AdminParticipantRegister from './components/AdminParticipantRegister/AdminParticipantRegister';
import ForgotPassword from './components/Auth/ForgotPassword';
import ChangePassword from './components/Auth/ChangePassword';
import AppContext from './AppContext';
import { NotificationDataType } from './common/types';
import { NotificationFullScreen } from './common/components/NotificationFullScreen/NotificationFullScreen';
import MatDisplay from './components/MatDisplay/MatDisplay';
import MatControlPage from './components/MatControl/MatControlPage';
import { DocumentsPage } from './components/Documents/DocumentsPage';
import { canAccessDocuments } from './utils/documentsAccess';
import './styles.scss';
import { DisappearingNotification, DisappearingNotificationType } from './common/components/DisappearingNotification/DisappearingNotification';

// NavBar component with authentication state
const NavBar = () => {
  const { user, logout, isAdmin, isOwner } = useAuth();

  return (
    <header className="header">
      <Link to="/" className="header__brand">
        <span className="header__brand-mark">40</span>
        <span className="header__brand-text">
          <span className="header__brand-name">JUDO40</span>
          <span className="header__brand-tagline">Турниры по дзюдо</span>
        </span>
      </Link>
      <nav className="nav">
        <ul>
          <li><Link to="/">Главная</Link></li>
          <li className="nav__sep" aria-hidden="true">/</li>
          <li><Link to="/tournaments">Турниры</Link></li>
          {user ? (
            <>
              {isAdmin() && <li><Link to="/persons">Участники</Link></li>}
              {isAdmin() && <li><Link to="/admin">Панель администратора</Link></li>}
              {isAdmin() && <li><Link to="/admin/displays">Табло</Link></li>}
              {canAccessDocuments(user, isOwner()) && <li><Link to="/documents">Документы</Link></li>}
              <li className="profile-menu">
                <div className="profile-menu__user-name">{user.firstName} {user.lastName}</div>
                <div className="profile-menu__dropdown">
                  <Link to={`/persons/${user.id}`}>Мой профиль</Link>
                  <Link to="/change-password">Сменить пароль</Link>
                  <button onClick={logout}>Выйти</button>
                </div>
              </li>
            </>
          ) : (
            <>
              <li><Link to="/login" className="nav__link-ghost">Вход</Link></li>
              <li><Link to="/register" className="nav__link-solid">Регистрация</Link></li>
            </>
          )}
        </ul>
      </nav>
    </header>
  );
};

// Обычная оболочка сайта (шапка/меню/подвал) — всё, кроме публичного табло /display/:matNumber,
// которое должно занимать весь экран монитора без навигации.
const AppShell: React.FC<{ notificationData?: NotificationDataType }> = ({ notificationData }) => (
  <div className="app">
    <NavBar />

    <main>
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/change-password" element={
          <AuthGuard>
            <ChangePassword />
          </AuthGuard>
        } />
        <Route path="/tournaments" element={<TournamentList />} />

        {/* Защищенные маршруты */}
        <Route path="/tournaments/create" element={
          <AuthGuard requiredRole="Админ">
            <CreateTournament />
          </AuthGuard>
        } />
        <Route path="/tournaments/:id" element={<TournamentDetail />} />
        <Route path="/tournaments/:id/edit" element={
          <AuthGuard requiredRole="Админ">
            <UpdateTournament />
          </AuthGuard>
        } />
        <Route path="/tournaments/:id/results" element={<LoginRequired><TournamentResults /></LoginRequired>} />
        <Route path="/brackets/:id" element={<LoginRequired><BracketView /></LoginRequired>} />
        <Route path="/tournaments/:id/admin-register" element={
          <AuthGuard requiredRole="Админ">
            <AdminParticipantRegister />
          </AuthGuard>
        } />
        <Route path="/persons" element={
          <AuthGuard requiredRole="Админ">
            <PersonList />
          </AuthGuard>
        } />
        <Route path="/persons/:id" element={
          <AuthGuard>
            <PersonDetail />
          </AuthGuard>
        } />
        <Route path="/persons/:id/edit" element={
          <AuthGuard>
            <UpdatePerson />
          </AuthGuard>
        } />

        <Route path="/admin" element={
          <AuthGuard requiredRole="Админ">
            <AdminDashboard />
          </AuthGuard>
        } />
        <Route path="/admin/displays" element={
          <AuthGuard requiredRole="Админ">
            <MatControlPage />
          </AuthGuard>
        } />
        <Route path="/documents" element={
          <AuthGuard>
            <DocumentsPage />
          </AuthGuard>
        } />

        <Route path="/privacy" element={<Privacy />} />
        <Route path="/terms" element={<Terms />} />

        {/* Страница 404, если нет совпадений */}
        <Route path="*" element={<NotFound />} />
      </Routes>
    </main>

    {notificationData && <NotificationFullScreen message={notificationData.message} />}

    <footer>
      <div className="footer__brand">
        <span className="footer__mark">40</span>
        <span className="footer__name">JUDO40</span>
      </div>
      <nav className="footer__links" aria-label="Правовая информация">
        <Link to="/privacy">Политика конфиденциальности</Link>
        <Link to="/terms">Пользовательское соглашение</Link>
      </nav>
      <p>&copy; {new Date().getFullYear()} JUDO40 — система управления турнирами по дзюдо</p>
    </footer>
  </div>
);

const App = () => {
  const [notificationData, setNotificationData] = React.useState<NotificationDataType | undefined>(undefined);
  const [disappearingNotification, setDisappearingNotification] = React.useState<DisappearingNotificationType | undefined>(undefined);
  const appContextValue = React.useMemo(() => ({
    notificationData,
    setNotificationData,
    disappearingNotification,
    setDisappearingNotification,
  }), [notificationData, disappearingNotification]);

  return (
    <AppContext.Provider value={appContextValue}>
      <AuthProvider>
        <Router>
          <Routes>
            {/* Табло зала: публичная страница без логина и без шапки/меню сайта */}
            <Route path="/display/:matNumber" element={<MatDisplay />} />
            <Route path="/*" element={<AppShell notificationData={notificationData} />} />
          </Routes>
          {disappearingNotification && (
            <DisappearingNotification
              message={disappearingNotification.message}
              transactionTime={disappearingNotification.transactionTime}
              top={disappearingNotification.top}
              bottom={disappearingNotification.bottom}
              left={disappearingNotification.left}
              right={disappearingNotification.right}
            />
          )}
        </Router>
      </AuthProvider>
    </AppContext.Provider>
  );
};

export default App;
